import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import {
  MembershipBenefit,
  TenantConfig,
  TenantLanguage,
  TenantModule,
} from '@models';
import { BehaviorSubject } from 'rxjs';
import { APP_TENANT_ID } from './app-tenant-id.token';
import {
  BrandingSeed,
  brandingSeedKey,
  clearBrandingSeed,
  readBrandingSeed,
  safeLocalStorage,
  toBrandingSeed,
  writeBrandingSeed,
} from './branding-cache';

/**
 * Outcome of the tenant branding load.
 *
 *  - `loading`     → the tenant doc read is still in flight (initial state).
 *  - `loaded`      → THE SERVER (or a warm offline cache holding the real doc)
 *                    returned the tenant doc and branding was applied.
 *  - `unavailable` → TERMINAL. THE SERVER ANSWERED and the tenant doc does NOT
 *                    exist in the active Firebase project (or there is no
 *                    tenantId at all). Nothing will ever arrive for this
 *                    tenant, here or in any other stream that reads from it.
 *  - `error`       → we could not get an answer from the server: the read
 *                    rejected (THE OFFLINE COLD START LANDS HERE — see below),
 *                    a permission error, or our own load timeout fired. The
 *                    tenant may well exist; this says nothing about the rest of
 *                    the app. A persisted seed may have been applied in this
 *                    state (see `seedApplied`).
 *
 * WHERE THE OFFLINE COLD START LANDS — `error`, always. With
 * `persistentLocalCache` and an EMPTY cache, an offline `getDoc()` does not
 * resolve with `exists() === false`: it REJECTS with `unavailable` ("Failed to
 * get document because the client is offline",
 * `@firebase/firestore@4.9.0/dist/index.node.cjs.js:27894`). So `unavailable`
 * here really does mean "the server told us there is no such tenant".
 *
 * `unavailable` and `error` MUST stay separate: consumers are allowed to give
 * up immediately on `unavailable`, but giving up on `error` would lock a
 * perfectly valid user out of the app because of a transient failure. Neither
 * of them may send the user to the maintenance screen: that is what got the app
 * rejected by Google Play as "Broken Functionality" (opening a demo install
 * with no data straight into a dead-end maintenance page).
 */
export type BrandingLoadState = 'loading' | 'loaded' | 'unavailable' | 'error';

/**
 * Ceiling for the tenant-doc read, in ms.
 *
 * 5 s. It only ever bites when Firestore neither answers nor gives up — the
 * offline case rejects on its own in milliseconds and a healthy read on a poor
 * 3G connection is well under this, so on every realistic path this constant is
 * invisible. It is kept deliberately shorter than the 10 s grace the guards
 * give a failed branding read (`BRANDING_ERROR_GRACE_MS`): those two DO stack
 * in the pathological "the device believes it is online, Firestore never
 * settles, and there is no session nor a persisted seed" case, and 5 + 10 is
 * the price of not hard-failing a user whose login was about to succeed.
 */
export const BRANDING_LOAD_TIMEOUT_MS = 5_000;

/**
 * How many times `retryIfUnresolved()` may re-attempt the branding read within
 * a session. Retries are event-driven (connectivity regained), so this is only
 * a stop for a flapping connection: without a cap, a device bouncing in and out
 * of coverage would fire a Firestore read on every flap. Reset to zero on every
 * successful load, so a long session that reconnects many times is fine.
 */
export const BRANDING_MAX_RETRIES = 3;

/**
 * Loads TenantConfig branding from Firestore at app startup and applies
 * Ionic CSS color variables to :root so that all components reflect the
 * tenant's colors without requiring a rebuild.
 *
 * Usage in app.config.ts:
 * ```ts
 * {
 *   provide: APP_INITIALIZER,
 *   useFactory: (b: BrandingService) => () => b.loadAndApply(),
 *   deps: [BrandingService],
 *   multi: true,
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly firestore = inject(Firestore);
  private readonly appTenantId = inject(APP_TENANT_ID, { optional: true });

  /** Emits the tenant's logo URL once loaded (null = not yet loaded or unavailable). */
  readonly logoUrl$ = new BehaviorSubject<string | null>(null);

  /** Emits the tenant's display name once loaded. */
  readonly tenantName$ = new BehaviorSubject<string | null>(null);

  /** Emits the tenant type once loaded (null = not yet loaded or unavailable). */
  readonly tenantType$ = new BehaviorSubject<'federation' | 'club' | null>(
    null,
  );

  /** Emits the tenant phone country code once loaded. Default: '+34'. */
  readonly phoneCountryCode$ = new BehaviorSubject<string>('+34');

  /** Emits the active modules for this tenant once loaded. */
  readonly modules$ = new BehaviorSubject<TenantModule[]>([]);

  /** Emits the tenant payment config once loaded. */
  readonly paymentConfig$ = new BehaviorSubject<
    TenantConfig['paymentConfig'] | null
  >(null);

  /** Emits the configurable membership benefits once loaded. Empty array = none defined. */
  readonly membershipBenefits$ = new BehaviorSubject<MembershipBenefit[]>([]);

  /** Emits the UI languages configured for this tenant. Defaults to ['es']. */
  readonly languages$ = new BehaviorSubject<TenantLanguage[]>(['es']);

  /**
   * Emits the tenant's contact and social media links once loaded.
   * Empty object = none configured. Channels with no value are omitted by the UI.
   */
  readonly social$ = new BehaviorSubject<NonNullable<TenantConfig['social']>>(
    {},
  );

  /**
   * State of the branding load attempt.
   *
   * WHY THIS MATTERS: the route guards (`tenant-shell.guard`, `module.guard`)
   * wait for a tenant type / tenant config before letting a navigation through.
   * When the tenant doc does not exist in the active Firebase project (e.g. the
   * runtime dev/pro toggle switched the app to a project where `tenants/{id}`
   * was never created) nothing ever emits: `tenantType$` stays `null`, there is
   * no error to catch, and the guards would hang forever → dead white screen.
   * This subject is the "nothing else is coming" signal those guards need in
   * order to take a decision instead of waiting indefinitely.
   */
  readonly loadState$ = new BehaviorSubject<BrandingLoadState>('loading');

  /**
   * True only when THE SERVER confirmed the tenant doc is missing. A failed or
   * offline read is NOT included on purpose (see `BrandingLoadState`).
   */
  get isUnavailable(): boolean {
    return this.loadState$.value === 'unavailable';
  }

  /**
   * Last-known branding persisted from a previous successful read, if any.
   * Populated at the start of `loadAndApply()` and refreshed after every
   * successful read. It is a seed, never a truth (see `branding-cache.ts`).
   */
  private _seed: BrandingSeed | null = null;

  /** Storage key of `_seed` for the active tenant + Firebase project. */
  private _seedKey: string | null = null;

  /**
   * True when the currently exposed branding comes from `localStorage` instead
   * of a server read. Diagnostics + lets consumers know the values are a best
   * guess. When this is true `loadState$` is `'error'`, never `'loaded'`.
   */
  seedApplied = false;

  /**
   * Best tenant type available RIGHT NOW: the one that was read, or the last
   * one we ever saw for this tenant in this Firebase project. Used by the shell
   * guards to pick `/home` vs `/tabs` in degraded mode instead of dead-ending.
   */
  get lastKnownTenantType(): 'federation' | 'club' | null {
    return this.tenantType$.value ?? this._seed?.type ?? null;
  }

  /** Firebase project the branding read was performed against (diagnostics). */
  get firebaseProjectId(): string | null {
    return this.firestore.app?.options?.projectId ?? null;
  }

  /** Synchronous check — safe to call after tenant guard has run. */
  hasModule(m: TenantModule): boolean {
    return this.modules$.value.includes(m);
  }

  /** Guards against overlapping retries (see `retryIfUnresolved`). */
  private _retryInFlight = false;

  /** Retries already spent, capped by `BRANDING_MAX_RETRIES`. */
  private _retryCount = 0;

  /**
   * Re-runs the branding load IF the previous attempt did not reach the server.
   *
   * WHY THIS EXISTS: `loadAndApply()` is a ONE-SHOT `getDoc()` fired during
   * bootstrap. On a cold start with no network it fails and `loadState$` stays
   * `'error'` FOR THE WHOLE SESSION — nobody ever retried it. Observed on a
   * real device: airplane mode on, app opened, airplane mode off without
   * leaving the app → connectivity is perfect but branding is still stuck in
   * `'error'`, so the guards keep taking the degraded path forever. Call this
   * when connectivity comes back (see `app.ts checkNetwork`).
   *
   * It only affects state — it never navigates. A user quietly browsing when
   * the network returns must not be yanked to another route; the point is that
   * `tenantType$`, `modules$`, `logoUrl$`, `languages$` and `loadState$` become
   * correct for whatever they do next.
   *
   * Loop-safe by construction: a no-op when the branding is already `'loaded'`,
   * when a retry is already running, or once `BRANDING_MAX_RETRIES` attempts
   * have been spent. Every successful load resets the counter.
   *
   * `'unavailable'` IS NOT RETRIED, on purpose. That state means the server
   * answered and the tenant doc is not in this Firebase project, and the type
   * above documents it as TERMINAL; retrying it would contradict the contract
   * every consumer reads, and reconnecting changes nothing about a tenant that
   * does not exist (a tenant created mid-session is picked up on the next
   * launch). Only `'error'` — "we never got an answer" — is worth retrying.
   *
   * KNOWN, BOUNDED SIDE EFFECT: a retry puts `loadState$` back to `'loading'`,
   * so a shell guard evaluated during that window neither resolves from
   * branding nor triggers `brandingShellFallback$` (which filters on
   * `'error'`); it waits for the retry, i.e. at most
   * `BRANDING_LOAD_TIMEOUT_MS`. This is deliberate and it is the CHEAPER of the
   * two options: keeping the state at `'error'` during the retry would let that
   * navigation fall through to the fallback and pay the full
   * `BRANDING_ERROR_GRACE_MS` instead, twice as long, to reach a worse answer
   * (degraded) than the one about to arrive.
   *
   * @returns `true` when a retry actually ran and ended in `'loaded'`.
   */
  async retryIfUnresolved(tenantIdOverride?: string): Promise<boolean> {
    const state: BrandingLoadState = this.loadState$.value;
    if (state !== 'error') return false;
    if (this._retryInFlight) return false;
    if (this._retryCount >= BRANDING_MAX_RETRIES) {
      console.warn(
        `[BrandingService] Branding retry budget exhausted ` +
          `(${BRANDING_MAX_RETRIES} attempts) — staying in degraded mode until ` +
          `the app is restarted.`,
      );
      return false;
    }

    this._retryInFlight = true;
    this._retryCount++;
    try {
      await this.loadAndApply(tenantIdOverride);
      const outcome: BrandingLoadState = this.loadState$.value;
      return outcome === 'loaded';
    } finally {
      this._retryInFlight = false;
    }
  }

  /**
   * Fetches the TenantConfig for the current app tenant and applies branding.
   * Safe to call before auth — uses public Firestore read, no auth required.
   *
   * @param tenantIdOverride  Optional tenant ID. When provided it takes
   *   precedence over `APP_TENANT_ID` (useful in multi-tenant portals where
   *   the tenantId is resolved at runtime from auth claims).
   */
  async loadAndApply(tenantIdOverride?: string): Promise<void> {
    const tenantId = tenantIdOverride ?? this.appTenantId;
    if (!tenantId) {
      this._markUnavailable(null, 'no tenantId configured');
      return;
    }

    this.loadState$.next('loading');
    // ALWAYS through `safeLocalStorage()`, never the `localStorage` global:
    // on an origin with storage disabled the property access itself throws, and
    // a throw anywhere in this method rejects the app initializer that awaits
    // it → `bootstrapApplication()` rejects → eternal splash. Same class of bug
    // this whole change exists to remove.
    const storage = safeLocalStorage();
    this._seedKey = brandingSeedKey(tenantId, this.firebaseProjectId);
    this._seed = readBrandingSeed(storage, this._seedKey);

    /** Set only on the success path; persisted after the try/catch. */
    let loadedSeedToPersist: BrandingSeed | null = null;

    try {
      const snapshot = await this._readTenantDoc(tenantId);

      if (!snapshot.exists()) {
        // Reaching this line means THE SERVER ANSWERED and the doc is not
        // there: offline reads never get here — with an empty cache `getDoc()`
        // REJECTS with `unavailable` ("Failed to get document because the
        // client is offline", @firebase/firestore 4.9.0) and lands in the
        // `catch` below, which marks `error`, not `unavailable`.
        // So a stored seed is stale by definition here — drop it, we must
        // never resurrect the branding of a tenant that no longer exists.
        clearBrandingSeed(storage, this._seedKey);
        this._seed = null;
        this._markUnavailable(tenantId, 'tenant document does not exist');
        return;
      }

      const config = snapshot.data() as TenantConfig;
      this.logoUrl$.next(config.branding.logoUrl ?? null);
      this.tenantName$.next(config.name ?? null);
      this.tenantType$.next(config.type ?? null);
      this.phoneCountryCode$.next(config.phoneCountryCode ?? '+34');
      this.modules$.next(config.modules ?? []);
      this.paymentConfig$.next(config.paymentConfig ?? null);
      this.membershipBenefits$.next(config.membershipBenefits ?? []);
      this.languages$.next(
        config.languages?.length ? config.languages : ['es'],
      );
      this.social$.next(config.social ?? {});
      this.seedApplied = false;
      this._retryCount = 0;

      // A real read ALWAYS wins over what was persisted.
      this._seed = toBrandingSeed(config);
      loadedSeedToPersist = this._seed;

      this.loadState$.next('loaded');
    } catch (e) {
      // Branding is non-critical — fail silently so app still loads.
      console.warn('[BrandingService] Could not load tenant branding', e);
      // ...but the failure MUST be observable: guards rely on `loadState$` to
      // know that no branding will ever arrive and stop waiting. Note this is
      // `error`, NOT `unavailable`: the tenant probably exists and the rest of
      // the app (claims, tenantConfig$) may still work perfectly, so nobody is
      // allowed to lock the user out because of this.
      this._applySeed();
      this._markError(tenantId, e);
    }

    // OUTSIDE the try/catch of the read, and deliberately so: persisting is a
    // side effect of a load that ALREADY SUCCEEDED. When this sat inside the
    // `try`, a storage failure here jumped into the `catch` and downgraded a
    // perfectly good `'loaded'` into `'error'` + `seedApplied = true` — state
    // corruption, not degradation. Failing to persist may cost us the seed on
    // the next cold start; it may never invalidate the data we just read.
    if (loadedSeedToPersist) {
      writeBrandingSeed(storage, this._seedKey, loadedSeedToPersist);
    }
  }

  /**
   * Reads `tenants/{id}` with a HARD CEILING on how long the app boot can be
   * held hostage by Firestore.
   *
   * WHY: `loadAndApply()` is wired as a `provideAppInitializer`, so Angular
   * awaits this promise before bootstrapping. Firestore retries a read forever
   * while it believes it is online (captive portals, DNS black holes, a proxy
   * that swallows the connection...) — a read that never settles means a
   * bootstrap that never finishes: no router, no route, no guard, splash screen
   * up forever. Nothing else in the boot path can rescue that, so the ceiling
   * has to live here.
   *
   * Timing out marks `error`, exactly like a rejection, and the app carries on
   * with the persisted seed (or, failing that, in degraded mode).
   */
  private async _readTenantDoc(tenantId: string) {
    const ref = doc(this.firestore, `tenants/${tenantId}`);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () =>
          reject(
            new Error(
              `branding read timed out after ${BRANDING_LOAD_TIMEOUT_MS}ms`,
            ),
          ),
        BRANDING_LOAD_TIMEOUT_MS,
      );
    });
    try {
      return await Promise.race([getDoc(ref), timeout]);
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Applies the persisted seed (if any) so the app can boot with the tenant's
   * type, logo, name, modules and languages even with no network at all.
   *
   * DELIBERATE: this NEVER sets `loadState$` to `'loaded'`. The caller marks
   * `'error'` right after, because we have not talked to the server and
   * `'loaded'` is a promise consumers rely on ("this is what the tenant doc
   * says"). `'error'` already means "no server answer, the tenant may exist",
   * which is exactly the situation; the only difference a seed makes is that
   * `tenantType$` & friends now carry a plausible value, so the guards resolve
   * immediately instead of waiting out the grace period. `seedApplied` tells
   * the two apart when it matters.
   *
   * Values NOT part of the seed (paymentConfig, membershipBenefits, social) are
   * left at their defaults on purpose: they drive money and content, and a
   * stale copy is worse than none.
   */
  private _applySeed(): void {
    if (!this._seed) return;
    this.logoUrl$.next(this._seed.logoUrl);
    this.tenantName$.next(this._seed.tenantName);
    this.tenantType$.next(this._seed.type);
    this.phoneCountryCode$.next(this._seed.phoneCountryCode);
    this.modules$.next(this._seed.modules);
    this.languages$.next(this._seed.languages);
    this.seedApplied = true;
    console.warn(
      `[BrandingService] Applied persisted branding seed (saved at ` +
        `${new Date(this._seed.savedAt).toISOString()}) — the app is starting in ` +
        `degraded mode with last-known tenant data.`,
    );
  }

  /**
   * Flags the tenant as confirmed missing (terminal) and logs a diagnosable
   * error naming the tenant and the Firebase project that was queried.
   * The projectId is key: with the runtime dev/pro toggle the very same binary
   * can point to two different projects, and "tenant missing" almost always
   * means "you are talking to the wrong project".
   */
  private _markUnavailable(tenantId: string | null, reason: string): void {
    console.error(
      `[BrandingService] Tenant branding unavailable — tenantId="${tenantId ?? '(none)'}", ` +
        `firebaseProjectId="${this.firebaseProjectId ?? '(unknown)'}": ${reason}. ` +
        `The app will keep running with no tenant configuration.`,
    );
    this.loadState$.next('unavailable');
  }

  /**
   * Flags the branding read as failed. Deliberately a DIFFERENT state from
   * `unavailable`: the tenant is not known to be missing, so consumers must
   * keep waiting for their own data sources instead of giving up.
   */
  private _markError(tenantId: string, error: unknown): void {
    console.error(
      `[BrandingService] Tenant branding read FAILED (tenant may still exist) — ` +
        `tenantId="${tenantId}", ` +
        `firebaseProjectId="${this.firebaseProjectId ?? '(unknown)'}".`,
      error,
    );
    this.loadState$.next('error');
  }

  getAuthErrorMessage(error: string): string {
    switch (error) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Nombre de usuario o contraseña incorrecta';
      case 'auth/invalid-email':
        return 'Email inválido';
      case 'auth/email-already-exists':
      case 'auth/email-already-in-use':
        return 'El email ya está en uso';
      case 'auth/account-exists-with-different-credential':
        return 'El usuario ya existe con diferentes credenciales';
      case 'auth/weak-password':
        return 'Contraseña débil. Debe contener como mínimo 6 caracteres';
      case 'auth/internal-error':
      default:
        return 'Error desconocido';
    }
  }

  /**
   * Applies tenant primary color as CSS custom properties on :root.
   * Call this after loading TenantConfig — works at runtime without rebuild.
   *
   * Sets:
   *   --mp-primary        → primaryColor
   *   --mp-primary-dark   → primaryColor darkened ~20%
   *   --mp-primary-light  → primaryColor at 12% opacity (dark-theme safe)
   *
   * @param primaryColor  Hex color string (e.g. '#adff2f' or '#2563eb').
   */
  applyBrandingCss(primaryColor: string): void {
    if (!primaryColor?.startsWith('#') || primaryColor.length < 7) return;
    const root = document.documentElement;
    root.style.setProperty('--mp-primary', primaryColor);
    root.style.setProperty(
      '--mp-primary-dark',
      this._darken(primaryColor, 0.2),
    );
    root.style.setProperty(
      '--mp-primary-light',
      this._hexToRgba(primaryColor, 0.12),
    );
  }

  private _darken(hex: string, ratio: number): string {
    const [r, g, b] = this._hexToRgb(hex);
    const d = (c: number) =>
      Math.max(0, Math.round(c * (1 - ratio)))
        .toString(16)
        .padStart(2, '0');
    return `#${d(r)}${d(g)}${d(b)}`;
  }

  private _hexToRgba(hex: string, alpha: number): string {
    const [r, g, b] = this._hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private _hexToRgb(hex: string): [number, number, number] {
    return [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
  }
}
